// frontend/src/components/SubtaskGenerationModal.tsx
/**
 * Modal component for displaying subtask generation progress.
 * 
 * Shows real-time progress as Celery processes action items in the background.
 * Handles completion, errors, and retry functionality.
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Chip,
  Stack,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { auditAPI } from '../services/api';

// Types for subtask generation status
interface SubtaskGenerationStatus {
  task_id: string;
  status: 'pending' | 'processing' | 'completed' | 'completed_with_errors' | 'failed';
  total: number;
  processed: number;
  success: number;
  failed: number;
  progress_percentage: number;
  can_retry: boolean;
  retry_count: number;
  max_retries: number;
  error_message: string | null;
  is_complete: boolean;
}

interface SubtaskGenerationModalProps {
  open: boolean;
  taskId: string | null;
  onClose: () => void;
  onComplete: () => void;
}

const SubtaskGenerationModal: React.FC<SubtaskGenerationModalProps> = ({
  open,
  taskId,
  onClose,
  onComplete,
}) => {
  const [status, setStatus] = useState<SubtaskGenerationStatus | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Poll for status updates
   * Note: onComplete/onClose are intentionally NOT in the dependency array
   * to prevent polling restarts when parent re-renders with new function references.
   * We use refs to access the latest callbacks without triggering re-creation.
   */
  const onCompleteRef = React.useRef(onComplete);
  const onCloseRef = React.useRef(onClose);
  
  // Track auto-close timeout to prevent race conditions
  const autoCloseTimeoutRef = React.useRef<number | null>(null);

  // Keep refs up-to-date with latest callbacks
  useEffect(() => {
    onCompleteRef.current = onComplete;
    onCloseRef.current = onClose;
  }, [onComplete, onClose]);

  const pollStatus = useCallback(async () => {
    if (!taskId) return;

    try {
      const data = await auditAPI.getSubtaskGenerationStatus(taskId);
      setStatus(data);
      setError(null);

      // Stop polling if task is complete
      if (data.is_complete) {
        setIsPolling(false);
        
        // Clear any existing auto-close timeout to prevent duplicates
        if (autoCloseTimeoutRef.current) {
          clearTimeout(autoCloseTimeoutRef.current);
        }
        
        // Auto-close modal after a short delay to show success state
        autoCloseTimeoutRef.current = setTimeout(() => {
          onCompleteRef.current();
          onCloseRef.current();
          autoCloseTimeoutRef.current = null;
        }, 1500); // 1.5 second delay to show completion message
      }
    } catch (err: any) {
      console.error('Error polling status:', err);
      setError(err.response?.data?.error || 'Failed to get status');
      setIsPolling(false);
    }
  }, [taskId]); // Only taskId in dependencies - stable reference

  // Start polling when modal opens with a task
  useEffect(() => {
    if (open && taskId && !status?.is_complete) {
      setIsPolling(true);
      pollStatus(); // Initial fetch
    }

    return () => {
      setIsPolling(false);
      // Clean up any pending auto-close timeout
      if (autoCloseTimeoutRef.current) {
        clearTimeout(autoCloseTimeoutRef.current);
        autoCloseTimeoutRef.current = null;
      }
    };
  }, [open, taskId, pollStatus, status?.is_complete]);

  // Polling interval
  useEffect(() => {
    if (!isPolling) return;

    const interval = setInterval(pollStatus, 1500); // Poll every 1.5 seconds

    return () => clearInterval(interval);
  }, [isPolling, pollStatus]);

  // Handle retry
  const handleRetry = async () => {
    if (!taskId) return;

    setIsRetrying(true);
    try {
      await auditAPI.retrySubtaskGeneration(taskId);
      setIsPolling(true);
      // Reset status to show pending state
      setStatus(prev => prev ? { ...prev, status: 'pending', is_complete: false } : null);
    } catch (err: any) {
      console.error('Error retrying:', err);
      setError(err.response?.data?.error || 'Failed to retry');
    } finally {
      setIsRetrying(false);
    }
  };

  // Handle close/continue
  const handleClose = () => {
    // Clear any pending auto-close timeout to prevent duplicate callbacks
    if (autoCloseTimeoutRef.current) {
      clearTimeout(autoCloseTimeoutRef.current);
      autoCloseTimeoutRef.current = null;
    }
    
    if (status?.is_complete) {
      onComplete();
    }
    onClose();
  };

  // Get status icon and color
  const getStatusDisplay = () => {
    if (!status) return { icon: <CircularProgress size={24} />, color: 'info' as const, text: 'Initializing...' };

    switch (status.status) {
      case 'pending':
        return { icon: <CircularProgress size={24} />, color: 'info' as const, text: 'Queued...' };
      case 'processing':
        return { icon: <CircularProgress size={24} />, color: 'info' as const, text: 'Generating subtasks...' };
      case 'completed':
        return { icon: <CheckCircleIcon color="success" />, color: 'success' as const, text: 'Completed successfully!' };
      case 'completed_with_errors':
        return { icon: <WarningIcon color="warning" />, color: 'warning' as const, text: 'Completed with some errors' };
      case 'failed':
        return { icon: <ErrorIcon color="error" />, color: 'error' as const, text: 'Generation failed' };
      default:
        return { icon: <CircularProgress size={24} />, color: 'info' as const, text: 'Processing...' };
    }
  };

  const statusDisplay = getStatusDisplay();

  return (
    <Dialog
      open={open}
      onClose={status?.is_complete ? handleClose : undefined}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={!status?.is_complete}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {statusDisplay.icon}
        <span>Generating Subtasks</span>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ py: 2 }}>
          {/* Status message */}
          <Typography variant="body1" gutterBottom>
            {statusDisplay.text}
          </Typography>

          {/* Progress bar */}
          {status && !status.is_complete && (
            <Box sx={{ mt: 3, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Progress
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {status.processed} of {status.total} action items
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={status.progress_percentage}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          )}

          {/* Completion stats */}
          {status?.is_complete && (
            <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
              <Chip
                icon={<CheckCircleIcon />}
                label={`${status.success} successful`}
                color="success"
                variant="outlined"
              />
              {status.failed > 0 && (
                <Chip
                  icon={<ErrorIcon />}
                  label={`${status.failed} failed`}
                  color="error"
                  variant="outlined"
                />
              )}
            </Stack>
          )}

          {/* Error message */}
          {(error || status?.error_message) && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error || status?.error_message}
            </Alert>
          )}

          {/* Retry info */}
          {status?.can_retry && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Some subtasks failed to generate. You can retry the failed items.
              <br />
              <Typography variant="caption">
                Retry attempts: {status.retry_count} of {status.max_retries}
              </Typography>
            </Alert>
          )}

          {/* Safe to leave message */}
          {status && !status.is_complete && (
            <Alert severity="info" sx={{ mt: 2 }}>
              This process runs in the background. You can safely close this dialog or navigate away - 
              subtasks will continue generating.
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {/* Retry button */}
        {status?.can_retry && (
          <Button
            onClick={handleRetry}
            disabled={isRetrying}
            startIcon={isRetrying ? <CircularProgress size={16} /> : <RefreshIcon />}
            color="warning"
          >
            Retry Failed ({status.failed})
          </Button>
        )}

        {/* Close/Continue button */}
        <Button
          onClick={handleClose}
          variant={status?.is_complete ? 'contained' : 'outlined'}
          startIcon={status?.is_complete ? <CheckCircleIcon /> : <CloseIcon />}
          color={status?.status === 'completed' ? 'success' : 'primary'}
        >
          {status?.is_complete ? 'Continue to Analysis' : 'Continue in Background'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubtaskGenerationModal;

